import { useState, useEffect, useCallback, Fragment } from 'react';
import { apiJson, apiFetch } from '../../lib/api';

const FONT = "'JetBrains Mono', 'Fira Code', monospace";

interface UserEntry {
  github_id: string;
  github_login: string;
  email: string | null;
  avatar_url: string | null;
  display_name: string;
  last_seen_at: string;
  is_global_admin: boolean;
}

interface MembershipEntry {
  org_id: string;
  org_name: string;
  org_role: string;
}

export function UserManager() {
  const [users, setUsers] = useState<UserEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [memberships, setMemberships] = useState<MembershipEntry[]>([]);
  const [membershipsLoading, setMembershipsLoading] = useState(false);

  const loadUsers = useCallback(() => {
    apiJson<{ users: UserEntry[] }>('/api/admin/users')
      .then(d => {
        setUsers(d.users || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const toggleAdmin = async (userId: string, currentAdmin: boolean) => {
    await apiFetch(`/api/admin/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ is_global_admin: !currentAdmin }),
    });
    loadUsers();
  };

  const toggleExpandUser = (userId: string) => {
    if (expandedUser === userId) {
      setExpandedUser(null);
      setMemberships([]);
    } else {
      setExpandedUser(userId);
      setMembershipsLoading(true);
      apiJson<{ memberships: MembershipEntry[] }>(`/api/admin/users/${userId}/memberships`)
        .then(d => {
          setMemberships(d.memberships || []);
          setMembershipsLoading(false);
        })
        .catch(() => {
          setMemberships([]);
          setMembershipsLoading(false);
        });
    }
  };

  if (loading) {
    return <div style={{ color: '#6272a4', fontSize: 13, padding: 24, fontFamily: FONT }}>Loading users...</div>;
  }

  return (
    <div>
      <div style={{ marginBottom: 16, fontSize: 11, color: '#6272a4', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {users.length} registered user{users.length !== 1 ? 's' : ''}
      </div>

      <div style={{
        background: '#1e1e2e', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8,
        overflow: 'hidden',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 10, color: '#6272a4', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>User</th>
              <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 10, color: '#6272a4', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email</th>
              <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 10, color: '#6272a4', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</th>
              <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 10, color: '#6272a4', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Last Seen</th>
              <th style={{ textAlign: 'right', padding: '10px 16px', fontSize: 10, color: '#6272a4', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Admin</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <Fragment key={u.github_id}>
                <tr
                  style={{
                    borderBottom: expandedUser === u.github_id ? 'none' : '1px solid rgba(255,255,255,0.03)',
                    transition: 'all 0.15s ease',
                    cursor: 'pointer',
                  }}
                  onClick={() => toggleExpandUser(u.github_id)}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.02)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <td style={{ padding: '10px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {u.avatar_url ? (
                        <img src={u.avatar_url} width={28} height={28} style={{ borderRadius: '50%' }} />
                      ) : (
                        <div style={{
                          width: 28, height: 28, borderRadius: '50%',
                          background: 'rgba(79,143,255,0.12)', display: 'flex',
                          alignItems: 'center', justifyContent: 'center',
                          fontSize: 12, color: '#4f8fff', fontFamily: FONT,
                        }}>
                          {u.github_login[0]?.toUpperCase()}
                        </div>
                      )}
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 13, color: '#f8f8f2', fontWeight: 500 }}>
                            {u.display_name}
                          </span>
                          <svg viewBox="0 0 24 24" width={10} height={10} fill="none" stroke="#6272a4" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ transform: expandedUser === u.github_id ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s ease' }}>
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </div>
                        <div style={{ fontSize: 11, color: '#6272a4' }}>@{u.github_login}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '10px 16px', fontSize: 11, color: '#a8b2b8' }}>
                    {u.email || <span style={{ color: '#6272a4' }}>-</span>}
                  </td>
                  <td style={{ padding: '10px 16px' }}>
                    {u.is_global_admin && (
                      <span style={{
                        fontSize: 10, padding: '2px 8px', borderRadius: 4,
                        background: 'rgba(189,147,249,0.1)', border: '1px solid rgba(189,147,249,0.25)', color: '#bd93f9',
                        textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: FONT,
                      }}>
                        admin
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '10px 16px', fontSize: 11, color: '#6272a4' }}>
                    {new Date(u.last_seen_at).toLocaleString()}
                  </td>
                  <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleAdmin(u.github_id, u.is_global_admin); }}
                      style={{
                        padding: '4px 12px',
                        background: u.is_global_admin ? 'rgba(189,147,249,0.1)' : 'transparent',
                        border: `1px solid ${u.is_global_admin ? 'rgba(189,147,249,0.3)' : 'rgba(255,255,255,0.08)'}`,
                        borderRadius: 4,
                        color: u.is_global_admin ? '#bd93f9' : '#6272a4',
                        fontSize: 11,
                        fontFamily: FONT,
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      {u.is_global_admin ? 'Revoke Admin' : 'Make Admin'}
                    </button>
                  </td>
                </tr>

                {/* Expanded memberships row */}
                {expandedUser === u.github_id && (
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                    <td colSpan={5} style={{ padding: '0 16px 12px 56px' }}>
                      <div style={{
                        background: 'rgba(255,255,255,0.02)',
                        border: '1px solid rgba(255,255,255,0.06)',
                        borderRadius: 6,
                        padding: 12,
                      }}>
                        <div style={{
                          fontSize: 10, color: '#6272a4', textTransform: 'uppercase',
                          letterSpacing: '0.05em', marginBottom: 8, fontWeight: 600,
                        }}>
                          Organization Memberships
                        </div>
                        {membershipsLoading ? (
                          <div style={{ fontSize: 11, color: '#6272a4', fontFamily: FONT }}>Loading...</div>
                        ) : memberships.length > 0 ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {memberships.map(m => (
                              <div key={m.org_id} style={{
                                display: 'flex', alignItems: 'center', gap: 10,
                                padding: '6px 10px',
                                background: 'rgba(255,255,255,0.02)',
                                borderRadius: 4,
                                border: '1px solid rgba(255,255,255,0.04)',
                              }}>
                                <svg viewBox="0 0 24 24" width={12} height={12} fill="none" stroke="#4f8fff" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                  <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1" />
                                </svg>
                                <span style={{ fontSize: 12, color: '#f8f8f2', flex: 1 }}>{m.org_name}</span>
                                <span style={{
                                  fontSize: 10, padding: '2px 8px', borderRadius: 4,
                                  background: 'rgba(189,147,249,0.1)', border: '1px solid rgba(189,147,249,0.25)',
                                  color: '#bd93f9', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: FONT,
                                }}>
                                  {m.org_role}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{ fontSize: 11, color: '#6272a4', fontFamily: FONT }}>
                            Not a member of any organizations
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>

        {users.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40, color: '#6272a4', fontSize: 13, fontFamily: FONT }}>
            No users have signed in yet.
          </div>
        )}
      </div>
    </div>
  );
}
